import type { ComponentProps } from 'react'

import { CheckIcon, ChevronDownIcon } from '~/components/icons'
import { cn } from '~/lib/cn'

/**
 * A filter — `/specs/design.spec.md`, Controls: a quiet outlined pill, and one that is on fills
 * with a soft accent tint and leads with a check the off state does without. One primitive so
 * that every row of filters in the app reads as the same control, wherever it narrows.
 *
 * A chip that opens a choice rather than toggling says so with a trailing chevron, and takes
 * whatever a popover's trigger hands it.
 */
export function FilterChip({
  label,
  on,
  onToggle,
  opens = false,
  className,
  ...props
}: {
  label: string
  on: boolean
  onToggle?: () => void
  /** Whether pressing the chip opens a choice, rather than turning the filter on or off. */
  opens?: boolean
  className?: string | undefined
} & Omit<ComponentProps<'button'>, 'onToggle'>) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      {...props}
      className={cn(
        'flex h-8 shrink-0 items-center gap-1 rounded-full px-3.5 text-sm whitespace-nowrap transition-colors',
        on
          ? 'bg-accent/15 pl-2.5 text-accent ring-1 ring-accent/40 ring-inset'
          : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink',
        opens && 'pr-2.5',
        className,
      )}
    >
      {on && <CheckIcon aria-hidden className="size-4" />}
      {label}
      {opens && <ChevronDownIcon aria-hidden className="size-4" />}
    </button>
  )
}
